import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Chip,
  TextField,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { authService } from '../api/authService';
import { dipendenteService, negozioService, bloccoService, impostazioniGlobaliService } from '../api/services';
import { Dipendente, Negozio, User } from '../types/types';
import {
  AccessTime,
  Clear,
  TableChart,
  ChevronLeft,
  ChevronRight,
  Build,
  Save,
  Cancel,
  AddCircleOutline,
  DeleteOutline,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers';
import { apiRequest } from '../api/api';
import { useStatsDipendente } from '../api/queries';
import { getApiErrorMessage } from '../utils/apiError';

dayjs.locale('it');

const PAGE_COLOR = '#2196F3';

// Opzioni tipo orario (stesse della vista standard)
const TIPO_ORARIO_OPTIONS = [
  'LAVORATO',
  'CORSI AI DIPENDENTI',
  'CHIUSO DITTA',
  'RIPOSO',
  'ROL',
  'FERIE',
  'MALATTIA',
  'MATERNITA\'',
  'LEGGE 104 PERMESSO',
  'AVIS',
  'ALLATTAMENTO',
  'LUTTO',
  'PERMESSI PARENTALI',
  'SMART WORKING',
  'CONGEDO MATRIMONIALE',
  'PERMESSI ELETTORALI',
  'ASPETTATIVA',
  'PERMESSI STUDIO',
  'CHIUSO ORDINANZA',
  'QUARANTENA',
  'ASSENZA N.R. DL 127',
  'ASSENZA NON RETRIBUITA',
  'LEGGE 118 PERMESSO',
];

// ─── Costanti tipo orario (specchiate da MioOrario) ──────────────────────────

// Assenze a giornata intera (disabilitano i campi consuntivo, orari pre-impostati)
const TIPO_ASSENZA_PREIMPOSTA_ORARI = [
  'FERIE',
  'MALATTIA',
  'MATERNITA\'',
  'CONGEDO MATRIMONIALE',
  'ASPETTATIVA',
];

// Codici che disabilitano completamente i campi consuntivo
const CODICI_DISABILITA_CONSUNTIVO = [
  'ASSENZA NON RETRIBUITA',
];

// Assenze che permettono comunque di inserire orari consuntivo (permessi parziali/orari)
const ASSENZE_CON_CONSUNTIVO = [
  'ROL',
  'LEGGE 104 PERMESSO',
  'AVIS',
  'LUTTO',
  'PERMESSI PARENTALI',
  'PERMESSI ELETTORALI',
  'PERMESSI STUDIO',
  'LEGGE 118 PERMESSO',
  'CORSI AI DIPENDENTI',
  'SMART WORKING',
];

const TIPI_CONSOLIDAMENTO_OBBLIGATORIO = new Set([
  'LAVORATO',
  'ROL',
  'LEGGE 104 PERMESSO',
  'AVIS',
  'LUTTO',
  'PERMESSI PARENTALI',
  'PERMESSI ELETTORALI',
  'PERMESSI STUDIO',
  'LEGGE 118 PERMESSO',
  'CORSI AI DIPENDENTI',
  'SMART WORKING',
]);

// Tutti i tipi "assenza" (mostrano turno = ASSENZA oppure Riposo)
const TIPO_ASSENZA_LIST_COMPACT = [
  'CORSI AI DIPENDENTI',
  'CHIUSO DITTA',
  'ROL',
  'FERIE',
  'MALATTIA',
  'MATERNITA\'',
  'LEGGE 104 PERMESSO',
  'AVIS',
  'ALLATTAMENTO',
  'LUTTO',
  'PERMESSI PARENTALI',
  'CONGEDO MATRIMONIALE',
  'PERMESSI ELETTORALI',
  'ASPETTATIVA',
  'PERMESSI STUDIO',
  'CHIUSO ORDINANZA',
  'QUARANTENA',
  'ASSENZA N.R. DL 127',
  'ASSENZA NON RETRIBUITA',
  'LEGGE 118 PERMESSO',
  'SMART WORKING',
];

// Calcolo automatico (disable cons) = Categoria A + ROL + CODICI_DISABILITA
const CODICI_CALCOLO_AUTOMATICO = [
  ...TIPO_ASSENZA_PREIMPOSTA_ORARI,
  'ROL',
  ...CODICI_DISABILITA_CONSUNTIVO,
];

// Codici che richiedono calcolo automatico lato backend (non mandare orari cons)
// = stessa logica di isCodiceTipoOrarioSpeciale in MioOrario
const isCodiceTipoOrarioSpeciale = (codiceTipoOrario: string | null | undefined): boolean => {
  return codiceTipoOrario ? CODICI_CALCOLO_AUTOMATICO.includes(codiceTipoOrario) : false;
};

// Solo ROL invia orari consuntivi espliciti al backend
const shouldSendConsuntivoTimes = (tipoOrario: string, oraConsInizio: string, oraConsFine: string): boolean => {
  return tipoOrario === 'ROL' && !!oraConsInizio && !!oraConsFine;
};

/**
 * Restituisce true se i campi consuntivo devono essere disabilitati per questo tipo orario.
 * Stessa logica di isDisableConsuntivo in MioOrario.tsx
 */
const isDisableConsuntivo = (codiceTipoOrario: string | null | undefined): boolean => {
  if (!codiceTipoOrario) return false;
  // Massima priorità: ASSENZA NON RETRIBUITA disabilita del tutto
  if (CODICI_DISABILITA_CONSUNTIVO.includes(codiceTipoOrario)) return true;
  // Assenze con consuntivo permesso (ROL, AVIS, LUTTO, ecc.) → non disabilitare
  if (ASSENZE_CON_CONSUNTIVO.includes(codiceTipoOrario)) return false;
  // Altrimenti logica standard (Categoria A)
  return CODICI_CALCOLO_AUTOMATICO.includes(codiceTipoOrario);
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatHHMM = (time: string | null | undefined): string => {
  if (!time) return '';
  return time.substring(0, 5);
};

const calcOre = (inizio: string | null | undefined, fine: string | null | undefined): string => {
  if (!inizio || !fine) return '';
  const s = dayjs(`2000-01-01 ${inizio}`);
  let e = dayjs(`2000-01-01 ${fine}`);
  if (e.isBefore(s)) e = e.add(1, 'day');
  const diff = e.diff(s, 'minute');
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  if (h === 0 && m === 0) return '';
  return `${h}:${m.toString().padStart(2, '0')}`;
};

const sumOre = (orari: TurnoRow[]): string => {
  let tot = 0;
  orari.forEach((o) => {
    if (!o.oraConsInizio || !o.oraConsFine) return;
    if ((o.turno || '').toLowerCase() === 'riposo') return;
    const s = dayjs(`2000-01-01 ${o.oraConsInizio}`);
    let e = dayjs(`2000-01-01 ${o.oraConsFine}`);
    if (e.isBefore(s)) e = e.add(1, 'day');
    tot += e.diff(s, 'minute');
  });
  const h = Math.floor(tot / 60);
  const m = tot % 60;
  return `${h}:${m.toString().padStart(2, '0')}`;
};

const formatHoursNum = (value: number): string => {
  if (!value && value !== 0) return '0:00';
  const sign = value < 0 ? '-' : '';
  const totalMinutes = Math.round(Math.abs(value) * 60);
  const h = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${sign}${h}:${mins.toString().padStart(2, '0')}`;
};

const GIORNI_SHORT = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];

// Normalizza input orario libero → "HH:MM" (formato 24h)
// Esempi: "800" → "08:00", "830" → "08:30", "8" → "08:00", "08:30" → "08:30", "2030" → "20:30"
const normalizeTimeInput = (raw: string): string => {
  if (!raw || raw.trim() === '') return '';
  const clean = raw.replace(/[^0-9]/g, '');
  if (clean.length === 0) return '';

  let h: number, m: number;
  if (clean.length <= 2) {
    h = parseInt(clean, 10);
    m = 0;
  } else if (clean.length === 3) {
    h = parseInt(clean[0], 10);
    m = parseInt(clean.slice(1), 10);
  } else {
    h = parseInt(clean.slice(0, 2), 10);
    m = parseInt(clean.slice(2, 4), 10);
  }

  if (isNaN(h) || isNaN(m)) return raw;
  if (h > 23 || m > 59) return raw; // non valido, lascia invariato per mostrare l'errore
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
};

// Valida coppia orari: restituisce stringa di errore o null
const validateTimePair = (inizio: string, fine: string, tipoOrario: string): string | null => {
  if (tipoOrario === 'RIPOSO') return null;
  // Tipi speciali calcolati dal backend: orari facoltativi
  const TIPI_AUTO = ['FERIE', 'MALATTIA', 'CHIUSO DITTA', 'CHIUSO ORDINANZA', 'QUARANTENA',
    'PERMESSI PARENTALI', 'MATERNITA\'', 'CONGEDO MATRIMONIALE', 'ASPETTATIVA',
    'ASSENZA NON RETRIBUITA', 'ASSENZA N.R. DL 127', 'PERMESSI ELETTORALI', 'PERMESSI STUDIO'];
  if (TIPI_AUTO.includes(tipoOrario)) return null;

  if (!tipoOrario) return 'Selezionare il Tipo Orario';
  if ((inizio && !fine) || (!inizio && fine)) return 'Inserire sia l\'orario di inizio che di fine, oppure lasciare entrambi vuoti';

  if (inizio && fine) {
    const timeRe = /^([01]?\d|2[0-3]):([0-5]\d)$/;
    if (!timeRe.test(inizio)) return `Orario non valido: "${inizio}" (usa formato HH:MM in 24h)`;
    if (!timeRe.test(fine)) return `Orario non valido: "${fine}" (usa formato HH:MM in 24h)`;
  }
  return null;
};

// ─── Tipi ─────────────────────────────────────────────────────────────────────

interface TurnoRow {
  id: number;
  neg: string;
  descrizione: string;
  data: string;
  giornoSettimana: string;
  turno: string | null;
  oraPrevInizio: string | null;
  oraPrevFine: string | null;
  oraConsInizio: string | null;
  oraConsFine: string | null;
  codiceTipoOrario: string | null;
  tipoOrario: string | null;
  annotazioni: string | null;
  blocco?: boolean;
}

interface EditData {
  tipoOrario: string;
  annotazioni: string;
  oraConsInizio: string;
  oraConsFine: string;
  oraPrevInizio?: string;
  oraPrevFine?: string;
}

interface DataControlloSuggestion {
  suggestedDate: string;
  lastNonConsolidatoDate: string;
}

const isTurnoNonConsolidato = (turno: TurnoRow): boolean => {
  if (!turno.codiceTipoOrario || !TIPI_CONSOLIDAMENTO_OBBLIGATORIO.has(turno.codiceTipoOrario)) return false;
  if (!turno.oraPrevInizio || !turno.oraPrevFine) return false;
  return !turno.oraConsInizio || !turno.oraConsFine;
};

const sortTurniByDateTime = (a: TurnoRow, b: TurnoRow): number => {
  const aDate = dayjs(`${a.data}T${(a.oraPrevInizio || '00:00').substring(0, 5)}`);
  const bDate = dayjs(`${b.data}T${(b.oraPrevInizio || '00:00').substring(0, 5)}`);
  if (aDate.isBefore(bDate)) return -1;
  if (aDate.isAfter(bDate)) return 1;
  return 0;
};

const buildDataControlloSuggestion = (
  turni: TurnoRow[],
  dataInizio: string,
  dataFine: string
): DataControlloSuggestion | null => {
  const start = dayjs(dataInizio);
  const end = dayjs(dataFine);

  const nelPeriodo = turni.filter((t) => {
    const d = dayjs(t.data);
    return !d.isBefore(start, 'day') && !d.isAfter(end, 'day');
  });
  if (nelPeriodo.length === 0) return null;

  const nonConsolidati = nelPeriodo.filter(isTurnoNonConsolidato).sort(sortTurniByDateTime);
  if (nonConsolidati.length === 0) return null;

  // Se ci sono piu turni non consolidati, prendi il piu vecchio nel periodo
  // e proponi il giorno precedente.
  const primoNonConsolidato = nonConsolidati[0];
  const suggestedFallback = dayjs(primoNonConsolidato.data).subtract(1, 'day');

  return {
    suggestedDate: suggestedFallback.format('YYYY-MM-DD'),
    lastNonConsolidatoDate: primoNonConsolidato.data,
  };
};

// ─── Colori ───────────────────────────────────────────────────────────────────

const tipoOrarioColor = (tipo: string | null | undefined): string => {
  if (!tipo) return 'transparent';
  const t = tipo.toUpperCase();
  if (t === 'RIPOSO') return 'rgba(200,200,200,0.45)';
  if (t === 'FERIE') return 'rgba(129,212,250,0.55)';
  if (t === 'MALATTIA') return 'rgba(239,154,154,0.55)';
  if (t === 'ROL') return 'rgba(174,213,129,0.55)';
  if (t.startsWith('LEGGE') || t.startsWith('PERMESS') || t.includes('104')) return 'rgba(206,147,216,0.55)';
  if (t === 'CHIUSO DITTA' || t === 'CHIUSO ORDINANZA') return 'rgba(255,204,128,0.55)';
  if (t.includes('SMART') || t.includes('LAVORO AGILE')) return 'rgba(128,222,234,0.55)';
  if (t === 'MATERNITA\'') return 'rgba(249,168,212,0.55)';
  if (t.includes('FERIE') || t.includes('CONGEDO') || t.includes('ASPETTATIVA')) return 'rgba(165,214,167,0.55)';
  return 'transparent';
};

const consBg = (cons: string | null, prev: string | null): string => {
  if (!cons) return 'rgba(76,175,80,0.25)';
  if (!prev) return 'rgba(244,67,54,0.25)';
  return cons.substring(0, 5) === prev.substring(0, 5)
    ? 'rgba(76,175,80,0.25)'
    : 'rgba(244,67,54,0.25)';
};

// ─── Componente principale ────────────────────────────────────────────────────

const OrarioDipendenteCompact: React.FC = () => {
  useEffect(() => {
    document.documentElement.dataset.turnoSource = 'ORARIO_DIPENDENTE';
    return () => { delete document.documentElement.dataset.turnoSource; };
  }, []);

  const currentUser: User | null = useMemo(() => authService.getCurrentUser(), []);
  const location = useLocation();
  const navigate = useNavigate();

  const [negozi, setNegozi] = useState<Negozio[]>([]);
  const [loadingNegozi, setLoadingNegozi] = useState(true);
  const [allDipendenti, setAllDipendenti] = useState<Dipendente[]>([]);
  const [loadingDip, setLoadingDip] = useState(false);

  const [selectedNeg, setSelectedNeg] = useState('');
  const [selectedDip, setSelectedDip] = useState('');
  const [selectedMonth, setSelectedMonth] = useState<dayjs.Dayjs>(dayjs().startOf('month'));

  const [orari, setOrari] = useState<TurnoRow[]>([]);
  const [loadingOrari, setLoadingOrari] = useState(false);
  const [orariError, setOrariError] = useState<string | null>(null);
  const [dipendenteInfo, setDipendenteInfo] = useState<Dipendente | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Editing inline
  const [editingRows, setEditingRows] = useState<Record<number, EditData>>({});
  const [savingRows, setSavingRows] = useState<Record<number, boolean>>({});
  const [timeErrors, setTimeErrors] = useState<Record<number, string>>({});

  // ── Blocco date (stessa logica di MioOrario) ──
  const [dataBloccoGlobale, setDataBloccoGlobale] = useState<dayjs.Dayjs | null>(null);
  const [dataControllo, setDataControllo] = useState<dayjs.Dayjs | null>(null);

  // ── Controllo Orari (DatePicker data controllo, stessa logica di OrarioDipendente) ──
  const canManageDataControllo = currentUser?.ruolo === 'Admin' || currentUser?.ruolo === 'Coordinatore';
  const [controlloDate, setControlloDate] = useState<dayjs.Dayjs | null>(dayjs());
  const [controlloWarning, setControlloWarning] = useState(false);
  const [savingControllo, setSavingControllo] = useState(false);
  const [suggestionBanner, setSuggestionBanner] = useState<DataControlloSuggestion | null>(null);
  const mesiNomi = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
  const nomeDipStats = (dipendenteInfo?.nome || '').trim();
  const meseNome = mesiNomi[selectedMonth.month()];
  const annoStats = selectedMonth.year();
  const { data: statsData, refetch: refetchStats } = useStatsDipendente(nomeDipStats, meseNome, annoStats);
  const stats = useMemo(() => {
    if (!statsData) return { oreContratto: 0, orePianificate: 0, oreConsuntivate: 0, assenzeContrattuali: 0, attuale: 0, differenzaAttuale: 0 };
    const s = statsData as any;
    const roundMin = (v: number) => Math.round((v ?? 0) * 60) / 60;
    return {
      oreContratto: roundMin(s.oreContratto),
      orePianificate: roundMin(s.orePianificate),
      oreConsuntivate: roundMin(s.oreConsuntivate),
      assenzeContrattuali: roundMin(s.assenzeContrattuali),
      attuale: roundMin(s.attuale),
      differenzaAttuale: roundMin(s.differenzaAttuale),
    };
  }, [statsData]);

  const canAccess = useMemo(() => {
    return !!currentUser && (
      currentUser.ruolo === 'Admin' ||
      currentUser.ruolo === 'Coordinatore' ||
      currentUser.abilitaInsOrari === true
    );
  }, [currentUser]);

  const dipendentiFiltrati = useMemo(() => {
    if (!selectedNeg) return allDipendenti;
    return allDipendenti.filter((d) => d.neg === selectedNeg);
  }, [allDipendenti, selectedNeg]);

  // ── Carica negozi ──
  useEffect(() => {
    if (!canAccess || !currentUser) return;
    const load = async () => {
      setLoadingNegozi(true);
      try {
        let list: Negozio[] = [];
        const isPriv = currentUser.ruolo === 'Admin' || currentUser.ruolo === 'Coordinatore';
        if (isPriv) {
          list = await negozioService.getAll();
        } else if (currentUser.abilitaInsOrari && currentUser.negozio) {
          const n = await negozioService.getById(currentUser.negozio);
          list = [n];
        }
        setNegozi(list);

        const params = new URLSearchParams(location.search);
        const qNeg = params.get('neg');
        const qDip = params.get('dip');
        const qMese = params.get('mese');
        const qAnno = params.get('anno');

        const parsedMese = qMese ? parseInt(qMese, 10) : NaN;
        const parsedAnno = qAnno ? parseInt(qAnno, 10) : NaN;
        if (!isNaN(parsedMese) && !isNaN(parsedAnno) && parsedMese >= 1 && parsedMese <= 12) {
          setSelectedMonth(dayjs().year(parsedAnno).month(parsedMese - 1).startOf('month'));
        }

        if (qNeg && list.some((n) => n.neg === qNeg)) setSelectedNeg(qNeg);
        else if (currentUser.negozio && list.some((n) => n.neg === currentUser.negozio)) {
          setSelectedNeg(currentUser.negozio);
        }
        if (qDip) setSelectedDip(qDip);
      } catch {
        setError('Errore nel caricamento dei negozi');
      } finally {
        setLoadingNegozi(false);
      }
    };
    load();
  }, [canAccess]);

  // ── Carica dipendenti ──
  useEffect(() => {
    if (!canAccess || !currentUser) return;
    const load = async () => {
      setLoadingDip(true);
      try {
        const isPriv = currentUser.ruolo === 'Admin' || currentUser.ruolo === 'Coordinatore';
        if (isPriv) {
          const all = await dipendenteService.getAll();
          all.sort((a, b) => a.nome.localeCompare(b.nome));
          setAllDipendenti(all);
        } else if (currentUser.abilitaInsOrari && currentUser.negozio) {
          const deps = await dipendenteService.getByNegozio(currentUser.negozio);
          deps.sort((a, b) => a.nome.localeCompare(b.nome));
          setAllDipendenti(deps);
        }
      } catch {
        setError('Errore nel caricamento dei dipendenti');
      } finally {
        setLoadingDip(false);
      }
    };
    load();
  }, [canAccess]);

  // ── Carica orari quando cambia dipendente o mese ──
  const selectedDipObj = useMemo(
    () => allDipendenti.find((d) => d.nome === selectedDip) || null,
    [allDipendenti, selectedDip]
  );

  useEffect(() => {
    setDipendenteInfo(selectedDipObj);
  }, [selectedDipObj]);

  // ── Carica dataBloccoGlobale e dataControllo del dipendente selezionato ──
  useEffect(() => {
    const loadBlocchi = async () => {
      try {
        const dataGlobale = await impostazioniGlobaliService.getDataBloccoGlobale();
        setDataBloccoGlobale(dataGlobale ? dayjs(dataGlobale) : null);
      } catch {
        setDataBloccoGlobale(null);
      }
      try {
        if (selectedDipObj?.codicePersonale) {
          const b = await bloccoService.getByCodice(
            selectedDipObj.codicePersonale,
            selectedMonth.month() + 1,
            selectedMonth.year()
          );
          setDataControllo(b?.dataControllo ? dayjs(b.dataControllo) : null);
          // Imposta sempre la data odierna nel picker (come in OrarioDipendente)
          setControlloDate(dayjs());
          setControlloWarning(b?.hasLavoratoNonConsolidato === true);
        } else {
          setDataControllo(null);
          setControlloDate(dayjs());
          setControlloWarning(false);
        }
      } catch {
        setDataControllo(null);
        setControlloDate(dayjs());
        setControlloWarning(false);
      }
    };
    loadBlocchi();
  }, [selectedDipObj, selectedMonth]);

  /**
   * Stessa logica di MioOrario:
   * - 'hard': data <= dataBloccoGlobale  → nessuno può modificare
   * - 'soft': data <= dataControllo      → Admin può modificare, Coordinatore/abilitaInsOrari no
   * - null: non bloccato
   */
  const getBlockType = useCallback((dateStr?: string | null): 'hard' | 'soft' | null => {
    if (!dateStr) return null;
    const d = dayjs(dateStr);
    if (dataBloccoGlobale && !d.isAfter(dataBloccoGlobale, 'day')) return 'hard';
    if (dataControllo   && !d.isAfter(dataControllo,    'day')) return 'soft';
    return null;
  }, [dataBloccoGlobale, dataControllo]);

  const isDateBlocked = useCallback((dateStr?: string | null): boolean => {
    return getBlockType(dateStr) !== null;
  }, [getBlockType]);

  // Admin può sempre modificare; Coordinatore/abilitaInsOrari NON possono su righe bloccate
  const canModifyBlockedRow = useCallback((dateStr?: string | null): boolean => {
    if (currentUser?.ruolo === 'Admin') return true;
    return getBlockType(dateStr) === null;
  }, [currentUser, getBlockType]);

  // ── Salva Data Controllo (stessa logica di DataControlloBox in OrarioDipendente) ──
  const handleSaveDataControllo = useCallback(async () => {
    if (!selectedDipObj?.codicePersonale || !controlloDate) return;
    setSavingControllo(true);
    try {
      if (currentUser?.ruolo === 'Coordinatore') {
        const dataInizioControllo = selectedMonth
          .startOf('month')
          .subtract(1, 'month')
          .startOf('month')
          .format('YYYY-MM-DD');
        const dataFineControllo = controlloDate.format('YYYY-MM-DD');
        const turniRaw = await apiRequest<any[]>(
          `/orari-generati/codice/${encodeURIComponent(selectedDipObj.codicePersonale)}?dataInizio=${dataInizioControllo}&dataFine=${dataFineControllo}`
        );
        const turni = (turniRaw || []).map((item: any) => ({
          id: item.id,
          neg: item.neg || '',
          descrizione: item.descrizione || '',
          data: item.data,
          giornoSettimana: item.giornoSettimana || '',
          turno: item.turno || null,
          oraPrevInizio: item.oraPrevInizio ? item.oraPrevInizio.substring(0, 5) : null,
          oraPrevFine: item.oraPrevFine ? item.oraPrevFine.substring(0, 5) : null,
          oraConsInizio: item.oraConsInizio ? item.oraConsInizio.substring(0, 5) : null,
          oraConsFine: item.oraConsFine ? item.oraConsFine.substring(0, 5) : null,
          codiceTipoOrario: item.codiceTipoOrario || null,
          tipoOrario: item.codiceTipoOrario || item.tipoOrario || null,
          annotazioni: item.annotazioni || null,
          blocco: !!item.blocco,
        } as TurnoRow));

        const suggestion = buildDataControlloSuggestion(turni, dataInizioControllo, dataFineControllo);
        if (suggestion && suggestion.suggestedDate !== dataFineControllo) {
          setSuggestionBanner(suggestion);
          return;
        }
      }

      const result = await bloccoService.setDataControllo(
        selectedDipObj.codicePersonale,
        controlloDate.format('YYYY-MM-DD'),
        selectedMonth.month() + 1,
        selectedMonth.year()
      );
      setControlloWarning(result.hasLavoratoNonConsolidato === true);
      // Aggiorna dataControllo per il blocco grigi
      setDataControllo(controlloDate);
      setSuggestionBanner(null);
    } catch (e) {
      console.error('Errore salvataggio data controllo', e);
    } finally {
      setSavingControllo(false);
    }
  }, [selectedDipObj, controlloDate, selectedMonth, currentUser?.ruolo]);

  const handleConfirmSuggestedDataControllo = useCallback(async () => {
    if (!selectedDipObj?.codicePersonale || !suggestionBanner) return;
    const suggestedDayjs = dayjs(suggestionBanner.suggestedDate);
    setSavingControllo(true);
    try {
      const result = await bloccoService.setDataControllo(
        selectedDipObj.codicePersonale,
        suggestedDayjs.format('YYYY-MM-DD'),
        selectedMonth.month() + 1,
        selectedMonth.year()
      );
      setControlloDate(suggestedDayjs);
      setDataControllo(suggestedDayjs);
      setControlloWarning(result.hasLavoratoNonConsolidato === true);
      setSuggestionBanner(null);
    } catch (e) {
      console.error('Errore salvataggio data controllo suggerita', e);
    } finally {
      setSavingControllo(false);
    }
  }, [selectedDipObj, selectedMonth, suggestionBanner]);

  const handleIgnoreSuggestionAndSaveControllo = useCallback(async () => {
    if (!selectedDipObj?.codicePersonale || !controlloDate) return;
    setSavingControllo(true);
    try {
      const result = await bloccoService.setDataControllo(
        selectedDipObj.codicePersonale,
        controlloDate.format('YYYY-MM-DD'),
        selectedMonth.month() + 1,
        selectedMonth.year()
      );
      setDataControllo(controlloDate);
      setControlloWarning(result.hasLavoratoNonConsolidato === true);
      setSuggestionBanner(null);
    } catch (e) {
      console.error('Errore salvataggio data controllo senza suggerimento', e);
    } finally {
      setSavingControllo(false);
    }
  }, [selectedDipObj, controlloDate, selectedMonth]);

  const fetchOrari = useCallback(async () => {
    if (!selectedDipObj?.codicePersonale) {
      setOrari([]);
      return;
    }
    setLoadingOrari(true);
    setOrariError(null);
    try {
      const dataInizio = selectedMonth.startOf('month').format('YYYY-MM-DD');
      const dataFine = selectedMonth.endOf('month').format('YYYY-MM-DD');
      const codice = encodeURIComponent(selectedDipObj.codicePersonale);
      const raw = await apiRequest<any[]>(
        `/orari-generati/codice/${codice}?dataInizio=${dataInizio}&dataFine=${dataFine}`
      );

      // Genera griglia vuota del mese
      const grid: TurnoRow[] = [];
      let cursor = selectedMonth.startOf('month');
      const end = selectedMonth.endOf('month');
      while (!cursor.isAfter(end, 'day')) {
        grid.push({
          id: -parseInt(cursor.format('YYYYMMDD')),
          neg: selectedDipObj.neg || '',
          descrizione: selectedDipObj.nome || '',
          data: cursor.format('YYYY-MM-DD'),
          giornoSettimana: cursor.format('dddd'),
          turno: null,
          oraPrevInizio: null,
          oraPrevFine: null,
          oraConsInizio: null,
          oraConsFine: null,
          codiceTipoOrario: null,
          tipoOrario: null,
          annotazioni: null,
        });
        cursor = cursor.add(1, 'day');
      }

      if (raw && raw.length > 0) {
        const byDate: Record<string, TurnoRow[]> = {};
        raw.forEach((item: any) => {
          if (!byDate[item.data]) byDate[item.data] = [];
          byDate[item.data].push({
            id: item.id,
            neg: item.neg || '',
            descrizione: item.descrizione || '',
            data: item.data,
            giornoSettimana: item.giornoSettimana || '',
            turno: item.turno || null,
            oraPrevInizio: item.oraPrevInizio ? item.oraPrevInizio.substring(0, 5) : null,
            oraPrevFine: item.oraPrevFine ? item.oraPrevFine.substring(0, 5) : null,
            oraConsInizio: item.oraConsInizio ? item.oraConsInizio.substring(0, 5) : null,
            oraConsFine: item.oraConsFine ? item.oraConsFine.substring(0, 5) : null,
            codiceTipoOrario: item.codiceTipoOrario || null,
            tipoOrario: item.codiceTipoOrario || item.tipoOrario || null,
            annotazioni: item.annotazioni || null,
            blocco: !!item.blocco,
          });
        });

        const merged: TurnoRow[] = [];
        grid.forEach((emptyDay) => {
          const turniGiorno = byDate[emptyDay.data];
          if (turniGiorno && turniGiorno.length > 0) {
            merged.push(...turniGiorno);
          } else {
            merged.push(emptyDay);
          }
        });
        // Ordina per data, poi per ora
        merged.sort((a, b) => {
          const da = a.data.localeCompare(b.data);
          if (da !== 0) return da;
          return (a.oraPrevInizio || '99:99').localeCompare(b.oraPrevInizio || '99:99');
        });
        setOrari(merged);
      } else {
        setOrari(grid);
      }
    } catch {
      setOrariError('Errore nel caricamento degli orari');
      setOrari([]);
    } finally {
      setLoadingOrari(false);
    }
  }, [selectedDipObj, selectedMonth]);

  useEffect(() => {
    fetchOrari();
  }, [fetchOrari]);

  // ── Editing handlers ──
  const handleStartEdit = useCallback((o: TurnoRow) => {
    if (o.id < 0) return; // righe fittizie non modificabili
    if (!canModifyBlockedRow(o.data)) return; // data bloccata non modificabile

    let prevInizio = o.oraPrevInizio || '';
    let prevFine   = o.oraPrevFine   || '';

    // Se il tipo è TIPO_ASSENZA_PREIMPOSTA_ORARI (es. MALATTIA) e non ha ancora gli orari previsti,
    // calcola automaticamente 00:00 → ore contrattuali
    const tipoCorrente = o.codiceTipoOrario || o.tipoOrario || '';
    if (TIPO_ASSENZA_PREIMPOSTA_ORARI.includes(tipoCorrente) && (!prevInizio || !prevFine) && dipendenteInfo) {
      const dayOfWeek = dayjs(o.data).day();
      let contractHours = 0;
      switch(dayOfWeek) {
        case 1: contractHours = dipendenteInfo.lunedi || 0; break;
        case 2: contractHours = dipendenteInfo.martedi || 0; break;
        case 3: contractHours = dipendenteInfo.mercoledi || 0; break;
        case 4: contractHours = dipendenteInfo.giovedi || 0; break;
        case 5: contractHours = dipendenteInfo.venerdi || 0; break;
        case 6: contractHours = dipendenteInfo.sabato || 0; break;
        case 0: contractHours = dipendenteInfo.domenica || 0; break;
      }
      prevInizio = '00:00';
      prevFine   = contractHours > 0
        ? dayjs().hour(0).minute(0).add(contractHours, 'hour').format('HH:mm')
        : '00:00';
      // Aggiorna subito la visualizzazione nella tabella
      setOrari(prev => prev.map(r => r.id === o.id
        ? { ...r, oraPrevInizio: prevInizio, oraPrevFine: prevFine }
        : r
      ));
    }

    setEditingRows(prev => ({
      ...prev,
      [o.id]: {
        tipoOrario: o.codiceTipoOrario || o.tipoOrario || 'LAVORATO',
        annotazioni: (o.annotazioni && o.annotazioni !== '__RIGA_DUPLICATA__') ? o.annotazioni : '',
        oraConsInizio: o.oraConsInizio || '',
        oraConsFine: o.oraConsFine || '',
        oraPrevInizio: prevInizio,
        oraPrevFine: prevFine,
      },
    }));
  }, [canModifyBlockedRow, dipendenteInfo]);

  const handleCancelEdit = useCallback((id: number) => {
    setEditingRows(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setTimeErrors(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const handleSaveEdit = useCallback(async (id: number) => {
    const editData = editingRows[id];
    if (!editData) return;

      // Se il tipo orario disabilita il consuntivo, azzera i campi cons prima di salvare
    const disableCons = isDisableConsuntivo(editData.tipoOrario);
    const effInizio = disableCons ? '' : editData.oraConsInizio;
    const effFine   = disableCons ? '' : editData.oraConsFine;

    // Normalizza prima di validare
    const normInizio = normalizeTimeInput(effInizio);
    const normFine = normalizeTimeInput(effFine);
    const normalized = { ...editData, oraConsInizio: normInizio, oraConsFine: normFine };

    // Valida il formato 24h (salta se il tipo non richiede orari)
    const validErr = validateTimePair(normInizio, normFine, editData.tipoOrario);
    if (validErr) {
      setTimeErrors(prev => ({ ...prev, [id]: validErr }));
      // Aggiorna lo stato con i valori normalizzati anche in caso di errore
      setEditingRows(prev => ({ ...prev, [id]: normalized }));
      return;
    }

    setTimeErrors(prev => { const n = { ...prev }; delete n[id]; return n; });
    setSavingRows(prev => ({ ...prev, [id]: true }));
    try {
      const params = new URLSearchParams();
      // Stessa logica di MioOrario: per tipi speciali (FERIE, MALATTIA, ecc.)
      // NON mandare gli orari cons → il backend li calcola automaticamente dal contratto
      if (editData.tipoOrario === 'RIPOSO') {
        params.append('oraConsInizio', '');
        params.append('oraConsFine', '');
      } else if (!isCodiceTipoOrarioSpeciale(editData.tipoOrario) || ASSENZE_CON_CONSUNTIVO.includes(editData.tipoOrario)) {
        // Se NON è speciale OPPURE è assenza con consuntivo (ROL, AVIS, LUTTO, ecc.) → manda orari
        if (normInizio) params.append('oraConsInizio', normInizio);
        if (normFine) params.append('oraConsFine', normFine);
      }
      if (editData.tipoOrario) params.append('codiceTipoOrario', editData.tipoOrario);
      params.append('annotazioni', editData.annotazioni || '');
      // Determina turno in base al tipo orario e all'ora di inizio consuntivo
      let turno = '';
      if (editData.tipoOrario === 'RIPOSO') {
        turno = 'Riposo';
      } else if (editData.tipoOrario && TIPO_ASSENZA_LIST_COMPACT.includes(editData.tipoOrario)) {
        turno = 'ASSENZA';
      } else if (normInizio) {
        const [h, m] = normInizio.split(':').map(Number);
        const tot = h * 60 + m;
        if (tot < 600) turno = 'Mattina';
        else if (tot < 810) turno = 'Intermezzo';
        else turno = 'Pomeriggio';
      }
      params.append('turno', turno);

      const updated = await apiRequest<any>(`/orari-generati/${id}/consuntivo?${params.toString()}`, { method: 'PUT' });

      // RIPOSO + Admin: seconda chiamata per azzerare anche gli orari previsti (uguale a MioOrario)
      let finalUpdated = updated;
      const currentUser = authService.getCurrentUser();
      if (editData.tipoOrario === 'RIPOSO' && currentUser?.ruolo === 'Admin') {
        const turnoCompleto = { ...updated, oraPrevInizio: null, oraPrevFine: null, oraConsInizio: null, oraConsFine: null };
        finalUpdated = await apiRequest<any>(`/orari-generati/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(turnoCompleto),
        });
      }

      // MALATTIA (e TIPO_ASSENZA_PREIMPOSTA_ORARI): persisti oraPrevInizio/oraPrevFine calcolate in onChange
      if (TIPO_ASSENZA_PREIMPOSTA_ORARI.includes(editData.tipoOrario) && editData.oraPrevInizio && editData.oraPrevFine) {
        const oraPrevInizioFmt = editData.oraPrevInizio.length === 5 ? editData.oraPrevInizio + ':00' : editData.oraPrevInizio;
        const oraPrevFineFmt   = editData.oraPrevFine.length === 5   ? editData.oraPrevFine   + ':00' : editData.oraPrevFine;
        const turnoCompleto = { ...finalUpdated, oraPrevInizio: oraPrevInizioFmt, oraPrevFine: oraPrevFineFmt };
        finalUpdated = await apiRequest<any>(`/orari-generati/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(turnoCompleto),
        });
      }

      // Stessa logica di MioOrario: safeTipoOrario evita che il backend ritorni 'ASSENZA' come codice
      const serverTipo = finalUpdated.codiceTipoOrario || finalUpdated.tipoOrario;
      const safeTipoOrario = serverTipo && serverTipo !== 'ASSENZA' ? serverTipo : editData.tipoOrario;

      const trim5 = (v: string | null | undefined): string | null =>
        v ? (v.length > 5 ? v.substring(0, 5) : v) : null;

      setOrari(prev => prev.map(o => o.id === id ? {
        ...o,
        // Aggiorna oraPrev dalla risposta (se null in risposta → null, non mantiene il vecchio)
        oraPrevInizio: finalUpdated.oraPrevInizio ? trim5(finalUpdated.oraPrevInizio) : null,
        oraPrevFine:   finalUpdated.oraPrevFine   ? trim5(finalUpdated.oraPrevFine)   : null,
        oraConsInizio: trim5(finalUpdated.oraConsInizio),
        oraConsFine:   trim5(finalUpdated.oraConsFine),
        codiceTipoOrario: safeTipoOrario,
        tipoOrario:       safeTipoOrario,
        annotazioni: finalUpdated.annotazioni ?? editData.annotazioni,
        turno: finalUpdated.turno || turno,
      } : o));

      setEditingRows(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      void refetchStats();
    } catch (err) {
      console.error('Errore nel salvataggio:', err);
      alert(getApiErrorMessage(err, 'Errore nel salvataggio'));
    } finally {
      setSavingRows(prev => { const n = { ...prev }; delete n[id]; return n; });
    }
  }, [editingRows, refetchStats]);

  const handleDuplicateRow = useCallback(async (o: TurnoRow) => {
    if (o.id < 0) return;
    try {
      const newTurno = {
        neg: o.neg,
        descrizione: o.descrizione,
        data: o.data,
        oraPrevInizio: null,
        oraPrevFine: null,
        giornoSettimana: o.giornoSettimana,
        turno: '',
        tipoOrario: 'LAVORATO',
        codiceTipoOrario: 'LAVORATO',
        annotazioni: '__RIGA_DUPLICATA__',
        utenteModifica: '',
      };
      const created = await apiRequest<any>('/orari-generati', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newTurno),
      });
      const normalized: TurnoRow = {
        id: created.id,
        neg: created.neg || o.neg,
        descrizione: created.descrizione || o.descrizione,
        data: created.data || o.data,
        giornoSettimana: created.giornoSettimana || o.giornoSettimana,
        turno: created.turno || null,
        oraPrevInizio: null,
        oraPrevFine: null,
        oraConsInizio: created.oraConsInizio ? created.oraConsInizio.substring(0, 5) : null,
        oraConsFine: created.oraConsFine ? created.oraConsFine.substring(0, 5) : null,
        codiceTipoOrario: created.codiceTipoOrario || 'LAVORATO',
        tipoOrario: created.codiceTipoOrario || 'LAVORATO',
        annotazioni: '__RIGA_DUPLICATA__',
        blocco: false,
      };
      setOrari(prev => {
        const idx = prev.findIndex(r => r.id === o.id);
        if (idx === -1) return [...prev, normalized];
        return [...prev.slice(0, idx + 1), normalized, ...prev.slice(idx + 1)];
      });
      void refetchStats();
    } catch (err) {
      console.error('Errore nella duplicazione:', err);
      alert('Errore nella duplicazione della riga');
    }
  }, [refetchStats]);

  const handleResetRowToBaseline = useCallback(async (o: TurnoRow) => {
    if (o.annotazioni !== '__RIGA_DUPLICATA__') return;
    if (!window.confirm('Sei sicuro di voler reimpostare questa riga al baseline RIPOSO?')) return;
    try {
      const baseline = await apiRequest<any>(`/orari-generati/${o.id}/baseline`, { method: 'PUT' });
      setOrari(prev => prev.map(r => r.id === o.id ? {
        ...r,
        turno: null,
        oraPrevInizio: null,
        oraPrevFine: null,
        oraConsInizio: null,
        oraConsFine: null,
        codiceTipoOrario: baseline.codiceTipoOrario || 'RIPOSO',
        tipoOrario: baseline.codiceTipoOrario || 'RIPOSO',
        annotazioni: null,
        blocco: false,
      } : r));
      void refetchStats();
    } catch (err) {
      console.error('Errore nel reset a baseline:', err);
      alert(getApiErrorMessage(err, 'Errore nel reset della riga a baseline'));
    }
  }, [refetchStats]);

  // ── Auto-save on click outside editing row ──
  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      const editingRowIds = Object.keys(editingRows);
      if (editingRowIds.length === 0) return;

      const editingRowId = parseInt(editingRowIds[0]);
      const rowElement = document.querySelector(`[data-row-id="${editingRowId}"]`);
      if (!rowElement) return;

      const target = event.target as HTMLElement;
      if (rowElement.contains(target)) return;

      // Non salvare se si clicca dentro un Select dropdown (MUI Portal)
      if (
        target.closest('.MuiPopover-root') ||
        target.closest('[role="presentation"]') ||
        target.closest('[role="listbox"]')
      ) return;

      // Cerca il pulsante Save e cliccalo
      setTimeout(() => {
        if (!editingRows[editingRowId]) return;
        const saveBtn = rowElement.querySelector('[data-save-btn="true"]') as HTMLButtonElement | null;
        if (saveBtn) {
          saveBtn.click();
        } else {
          handleSaveEdit(editingRowId);
        }
      }, 80);
    };

    document.addEventListener('mousedown', handleGlobalClick);
    return () => document.removeEventListener('mousedown', handleGlobalClick);
  }, [editingRows, handleSaveEdit]);

  // ── Clear handlers ──
  const handleClearNeg = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedNeg('');
  }, []);

  const handleClearDip = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedDip('');
  }, []);

  const handleDipChange = useCallback(
    (nome: string) => {
      setSelectedDip(nome);
      const dip = allDipendenti.find((d) => d.nome === nome);
      if (dip?.neg) setSelectedNeg(dip.neg);
    },
    [allDipendenti]
  );

  // ── Access guard ──
  if (!canAccess) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">Accesso non consentito</Alert>
      </Container>
    );
  }

  // ── Raggruppa righe per data per gestire più turni nello stesso giorno ──
  // (mostrando il rowspan visivo tramite bordi evidenziati)
  const totalOre = sumOre(orari);

  return (
    <Container
      maxWidth={false}
      sx={{
        mt: 0.5,
        mb: 2,
        px: { xs: 1, sm: 2 },
        maxWidth: '100vw',
        overflow: 'hidden',
        // Simula zoom 90%: scala il contenuto e compensa lo spazio residuo
        transform: 'scale(0.9)',
        transformOrigin: 'top left',
        width: 'calc(100% / 0.9)',
        // Il container scalato è più corto in altezza: margine negativo compensa
        marginBottom: 'calc(-10% + 16px)',
      }}
    >
      {/* ── Filtri ── */}
      <Paper sx={{ p: 1, mb: 1, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        {/* Negozio */}
        <FormControl sx={{ minWidth: 175, ml: { xs: 16, sm: 18 } }} size="small" disabled={loadingNegozi || !(currentUser?.ruolo === 'Admin' || currentUser?.ruolo === 'Coordinatore')}>
          <InputLabel sx={{ fontSize: '0.78rem' }}>Negozio</InputLabel>
          <Select
            value={selectedNeg}
            label="Negozio"
            onChange={(e) => setSelectedNeg(e.target.value)}
            sx={{ fontSize: '0.78rem' }}
            endAdornment={
              selectedNeg && (
                <IconButton size="small" onClick={handleClearNeg} sx={{ mr: 2 }}>
                  <Clear sx={{ fontSize: 14 }} />
                </IconButton>
              )
            }
          >
            {loadingNegozi && <MenuItem value=""><em><CircularProgress size={14} sx={{ mr: 1 }} /> Caricamento...</em></MenuItem>}
            {negozi.map((n) => (
              <MenuItem key={n.neg} value={n.neg} sx={{ fontSize: '0.78rem' }}>{n.neg} — {n.negozio}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Dipendente */}
        <FormControl sx={{ minWidth: 175 }} size="small">
          <InputLabel sx={{ fontSize: '0.78rem' }}>Dipendente</InputLabel>
          <Select
            value={selectedDip}
            label="Dipendente"
            onChange={(e) => handleDipChange(e.target.value)}
            sx={{ fontSize: '0.78rem' }}
            endAdornment={
              selectedDip && (
                <IconButton size="small" onClick={handleClearDip} sx={{ mr: 2 }}>
                  <Clear sx={{ fontSize: 14 }} />
                </IconButton>
              )
            }
          >
            {loadingDip && <MenuItem value=""><em><CircularProgress size={14} sx={{ mr: 1 }} /> Caricamento...</em></MenuItem>}
            {dipendentiFiltrati.map((d) => (
              <MenuItem key={d.codicePersonale} value={d.nome} sx={{ fontSize: '0.78rem' }}>{d.nome} ({d.neg})</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Mese / Anno con navigazione */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          <Tooltip title="Mese precedente">
            <IconButton size="small" onClick={() => setSelectedMonth((m) => m.subtract(1, 'month').startOf('month'))}>
              <ChevronLeft sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
            <DatePicker
              label="Mese / Anno"
              views={['year', 'month']}
              value={selectedMonth}
              onChange={(v) => v && setSelectedMonth(v.startOf('month'))}
              slotProps={{ textField: { size: 'small', sx: { width: 175, fontSize: '0.78rem' } } }}
            />
          </LocalizationProvider>
          <Tooltip title="Mese successivo">
            <IconButton size="small" onClick={() => setSelectedMonth((m) => m.add(1, 'month').startOf('month'))}>
              <ChevronRight sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ flex: 1 }} />

        {/* Pulsante Generazione Orari */}
        {selectedNeg && (
          <Tooltip title="Vai a Generazione Orari">
            <Button
              size="small"
              variant="outlined"
              color="success"
              startIcon={<Build sx={{ fontSize: 14 }} />}
              sx={{ fontSize: '0.72rem', py: 0.5, px: 1 }}
              onClick={() => {
                const m = selectedMonth.month() + 1;
                const a = selectedMonth.year();
                window.location.href = `/generazione-orario?neg=${encodeURIComponent(selectedNeg)}&mese=${m}&anno=${a}`;
              }}
            >
              Generazione
            </Button>
          </Tooltip>
        )}

        {/* Pulsante Vista Standard */}
        <Tooltip title="Vai alla vista standard">
          <Button
            size="small"
            variant="outlined"
            startIcon={<TableChart sx={{ fontSize: 14 }} />}
            sx={{ fontSize: '0.72rem', py: 0.5, px: 1 }}
            onClick={() => {
              const m = selectedMonth.month() + 1;
              const a = selectedMonth.year();
              const base = `/orario-dipendente?neg=${encodeURIComponent(selectedNeg)}&dip=${encodeURIComponent(selectedDip)}&mese=${m}&anno=${a}`;
              navigate(base);
            }}
          >
            Vista Standard
          </Button>
        </Tooltip>

        {error && <Alert severity="error" sx={{ flex: 1, py: 0 }}>{error}</Alert>}
      </Paper>

      {/* ── Errore orari ── */}
      {orariError && <Alert severity="error" sx={{ mb: 1 }}>{orariError}</Alert>}

      {/* ── Tabella compatta ── */}
      {selectedDipObj ? (
        <>
          {/* Intestazione dipendente + mese */}
          <Box sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: PAGE_COLOR }}>
              {selectedDipObj.nome}
            </Typography>
            <Chip label={selectedNeg} size="small" color="primary" variant="outlined" />
            <Typography variant="subtitle2" sx={{ color: '#555' }}>
              — {selectedMonth.format('MMMM YYYY')}
            </Typography>
            {loadingOrari && <CircularProgress size={18} />}
          </Box>

          <Paper
            sx={{
              width: '100%',
              overflow: 'hidden',
              boxShadow: 2,
              borderRadius: 1,
            }}
          >
            <TableContainer sx={{ width: '100%', overflow: 'hidden' }}>
              <Table
                size="small"
                sx={{
                  tableLayout: 'fixed',
                  width: '100%',
                  '& .MuiTableCell-root': {
                    borderRight: '1px solid rgba(224,224,224,1)',
                    fontSize: '0.72rem',
                    padding: '3px 4px',
                    verticalAlign: 'middle',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    fontWeight: 700,
                    backgroundColor: 'rgba(33,150,243,0.1)',
                    textAlign: 'center',
                    fontSize: '0.7rem',
                    lineHeight: 1.2,
                    padding: '4px 4px',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '5%' }}>Neg.</TableCell>
                    <TableCell sx={{ width: '4%' }}>#</TableCell>
                    <TableCell sx={{ width: '8%' }}>Data</TableCell>
                    <TableCell sx={{ width: '4%' }}>G</TableCell>
                    <TableCell sx={{ width: '6%', backgroundColor: 'rgba(255,235,59,0.25) !important' }}>Prev↑</TableCell>
                    <TableCell sx={{ width: '6%', backgroundColor: 'rgba(255,235,59,0.25) !important' }}>Prev↓</TableCell>
                    <TableCell sx={{ width: '7%' }}>Cons↑</TableCell>
                    <TableCell sx={{ width: '7%' }}>Cons↓</TableCell>
                    <TableCell sx={{ width: '14%' }}>Tipo Orario</TableCell>
                    <TableCell sx={{ width: '22%' }}>Annotazioni</TableCell>
                    <TableCell sx={{ width: '6%', backgroundColor: '#f5f5f5 !important' }}>Ore</TableCell>
                    <TableCell sx={{ width: '11%' }}>Azioni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orari.map((o, idx) => {
                    const d = dayjs(o.data);
                    const dow = d.day();
                    const isWeekend = dow === 0 || dow === 6;
                    const tipo = (o.codiceTipoOrario || o.tipoOrario || '').toUpperCase();
                    const isRiposo = tipo === 'RIPOSO' || (o.turno || '').toLowerCase() === 'riposo';
                    const isEditing = !!editingRows[o.id];
                    const editData = editingRows[o.id];
                    const isSaving = !!savingRows[o.id];
                    const isFake = o.id < 0;
                    const isDuplicate = o.annotazioni === '__RIGA_DUPLICATA__';
                    const isBlocked = isDateBlocked(o.data);
                    const canModifyBlocked = canModifyBlockedRow(o.data);

                    return (
                      <TableRow
                        key={`${o.id}-${o.data}-${idx}`}
                        data-row-id={o.id}
                        sx={{
                          backgroundColor: isEditing ? 'rgba(33,150,243,0.06)' : undefined,
                          outline: isEditing ? '2px solid rgba(33,150,243,0.4)' : undefined,
                          '&:hover': { filter: 'brightness(0.96)' },
                          ...(isBlocked && {
                            '& td': { backgroundColor: 'rgba(224, 224, 224, 0.6)', color: '#616161' }
                          }),
                          ...(isBlocked && !canModifyBlocked && { pointerEvents: 'none' }),
                        }}
                      >
                        {/* Negozio */}
                        <TableCell sx={{ textAlign: 'center', fontSize: '0.68rem', fontWeight: 600, color: '#1565c0' }}>
                          {o.neg || selectedDipObj?.neg || ''}
                        </TableCell>
                        {/* Giorno del mese */}
                        <TableCell sx={{ textAlign: 'center', fontWeight: isWeekend ? 700 : 400, color: dow === 0 ? '#d32f2f' : 'inherit' }}>
                          {d.date()}
                        </TableCell>
                        {/* Data */}
                        <TableCell sx={{ textAlign: 'center' }}>
                          {d.format('DD/MM/YYYY')}
                        </TableCell>
                        {/* Giorno settimana abbreviato */}
                        <TableCell sx={{ textAlign: 'center', fontWeight: 600, color: dow === 0 ? '#d32f2f' : dow === 6 ? '#f57c00' : '#555', fontSize: '0.68rem' }}>
                          {GIORNI_SHORT[dow]}
                        </TableCell>
                        {/* Ora Prev Inizio */}
                        <TableCell
                          onClick={() => !isEditing && !isFake && handleStartEdit(o)}
                          sx={{ textAlign: 'center', backgroundColor: 'rgba(255,235,59,0.2)', color: '#795548', cursor: !isFake ? 'pointer' : 'default' }}>
                          {tipo === 'CHIUSO DITTA' ? '' : formatHHMM(o.oraPrevInizio)}
                        </TableCell>
                        {/* Ora Prev Fine */}
                        <TableCell
                          onClick={() => !isEditing && !isFake && handleStartEdit(o)}
                          sx={{ textAlign: 'center', backgroundColor: 'rgba(255,235,59,0.2)', color: '#795548', cursor: !isFake ? 'pointer' : 'default' }}>
                          {tipo === 'CHIUSO DITTA' ? '' : formatHHMM(o.oraPrevFine)}
                        </TableCell>
                        {/* Ora Cons Inizio */}
                        <TableCell
                          onClick={() => !isEditing && !isFake && handleStartEdit(o)}
                          sx={{
                            textAlign: 'center',
                            backgroundColor: isEditing ? 'transparent' : (tipo === 'CHIUSO DITTA' ? 'transparent' : consBg(o.oraConsInizio, o.oraPrevInizio)),
                            fontWeight: o.oraConsInizio ? 600 : 400,
                            padding: isEditing ? '2px 2px' : undefined,
                            cursor: !isFake ? 'pointer' : 'default',
                          }}>
                          {isEditing ? (
                            isDisableConsuntivo(editData.tipoOrario) ? (
                              <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.68rem' }}>
                                {editData.tipoOrario === 'CHIUSO DITTA' ? '' : '—'}
                              </Typography>
                            ) : (
                            <TextField
                              autoFocus
                              type="time"
                              value={editData.oraConsInizio}
                              onChange={e => setEditingRows(prev => ({ ...prev, [o.id]: { ...prev[o.id], oraConsInizio: e.target.value } }))}
                              size="small"
                              inputProps={{ step: 300, style: { fontSize: '0.68rem', padding: '2px 4px', textAlign: 'center' } }}
                              sx={{ width: '100%' }}
                            />
                            )
                          ) : (
                            tipo === 'CHIUSO DITTA' ? '' : (isRiposo ? '—' : formatHHMM(o.oraConsInizio) || '--:--')
                          )}
                        </TableCell>
                        {/* Ora Cons Fine */}
                        <TableCell
                          onClick={() => !isEditing && !isFake && handleStartEdit(o)}
                          sx={{
                            textAlign: 'center',
                            backgroundColor: isEditing ? 'transparent' : (tipo === 'CHIUSO DITTA' ? 'transparent' : consBg(o.oraConsFine, o.oraPrevFine)),
                            fontWeight: o.oraConsFine ? 600 : 400,
                            padding: isEditing ? '2px 2px' : undefined,
                            cursor: !isFake ? 'pointer' : 'default',
                          }}>
                          {isEditing ? (
                            isDisableConsuntivo(editData.tipoOrario) ? (
                              <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.68rem' }}>
                                {editData.tipoOrario === 'CHIUSO DITTA' ? '' : '—'}
                              </Typography>
                            ) : (
                            <TextField
                              type="time"
                              value={editData.oraConsFine}
                              onChange={e => setEditingRows(prev => ({ ...prev, [o.id]: { ...prev[o.id], oraConsFine: e.target.value } }))}
                              size="small"
                              inputProps={{ step: 300, style: { fontSize: '0.68rem', padding: '2px 4px', textAlign: 'center' } }}
                              sx={{ width: '100%' }}
                            />
                            )
                          ) : (
                            tipo === 'CHIUSO DITTA' ? '' : (isRiposo ? '—' : formatHHMM(o.oraConsFine) || '--:--')
                          )}
                        </TableCell>
                        {/* Tipo Orario */}
                        <TableCell
                          onClick={() => !isEditing && !isFake && handleStartEdit(o)}
                          sx={{
                            textAlign: 'center',
                            padding: isEditing ? '2px 2px' : undefined,
                            cursor: !isFake ? 'pointer' : 'default',
                          }}>
                          {isEditing ? (
                            <Select
                              value={editData.tipoOrario}
                              onChange={e => {
                                const newTipo = e.target.value;
                                const extraFields: Partial<EditData> = {};

                                // Se il nuovo tipo disabilita il consuntivo, azzera i campi
                                if (isDisableConsuntivo(newTipo)) {
                                  extraFields.oraConsInizio = '';
                                  extraFields.oraConsFine = '';
                                }

                                // LOGICA MALATTIA (e tutti TIPO_ASSENZA_PREIMPOSTA_ORARI):
                                // Imposta oraPrevInizio = 00:00, oraPrevFine = ore contrattuali del giorno
                                if (TIPO_ASSENZA_PREIMPOSTA_ORARI.includes(newTipo) && dipendenteInfo) {
                                  const dayOfWeek = dayjs(o.data).day();
                                  let contractHours = 0;
                                  switch(dayOfWeek) {
                                    case 1: contractHours = dipendenteInfo.lunedi || 0; break;
                                    case 2: contractHours = dipendenteInfo.martedi || 0; break;
                                    case 3: contractHours = dipendenteInfo.mercoledi || 0; break;
                                    case 4: contractHours = dipendenteInfo.giovedi || 0; break;
                                    case 5: contractHours = dipendenteInfo.venerdi || 0; break;
                                    case 6: contractHours = dipendenteInfo.sabato || 0; break;
                                    case 0: contractHours = dipendenteInfo.domenica || 0; break;
                                  }
                                  const prevStart = '00:00';
                                  const prevEnd = contractHours > 0
                                    ? dayjs().hour(0).minute(0).add(contractHours, 'hour').format('HH:mm')
                                    : '00:00';
                                  extraFields.oraPrevInizio = prevStart;
                                  extraFields.oraPrevFine = prevEnd;
                                  // Aggiorna subito la visualizzazione nella tabella
                                  setOrari(prev => prev.map(r => r.id === o.id
                                    ? { ...r, oraPrevInizio: prevStart, oraPrevFine: prevEnd }
                                    : r
                                  ));
                                }

                                setEditingRows(prev => ({
                                  ...prev,
                                  [o.id]: {
                                    ...prev[o.id],
                                    tipoOrario: newTipo,
                                    ...extraFields,
                                  },
                                }));
                              }}
                              size="small"
                              sx={{ fontSize: '0.66rem', width: '100%', '.MuiSelect-select': { padding: '2px 4px' } }}
                            >
                              {TIPO_ORARIO_OPTIONS.map(opt => (
                                <MenuItem key={opt} value={opt} sx={{ fontSize: '0.72rem' }}>{opt}</MenuItem>
                              ))}
                            </Select>
                          ) : (
                            <Box sx={{
                              display: 'inline-block',
                              px: 0.5,
                              py: 0.1,
                              borderRadius: 0.5,
                              fontSize: '0.66rem',
                              fontWeight: 600,
                              backgroundColor: tipoOrarioColor(o.tipoOrario || o.codiceTipoOrario),
                              maxWidth: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {tipo || (isRiposo ? 'RIPOSO' : '—')}
                            </Box>
                          )}
                        </TableCell>
                        {/* Annotazioni */}
                        <TableCell
                          onClick={() => !isEditing && !isFake && handleStartEdit(o)}
                          sx={{
                            fontSize: '0.66rem',
                            color: '#444',
                            padding: isEditing ? '2px 2px' : undefined,
                            cursor: !isFake ? 'pointer' : 'default',
                          }}>
                          {isEditing ? (
                            <TextField
                              value={editData.annotazioni}
                              onChange={e => setEditingRows(prev => ({ ...prev, [o.id]: { ...prev[o.id], annotazioni: e.target.value } }))}
                              size="small"
                              inputProps={{ style: { fontSize: '0.66rem', padding: '2px 4px' } }}
                              sx={{ width: '100%' }}
                            />
                          ) : (
                            isDuplicate ? (
                              <Box component="span" sx={{ fontStyle: 'italic', color: '#9e9e9e', fontSize: '0.62rem' }}>riga aggiunta</Box>
                            ) : (o.annotazioni || '')
                          )}
                        </TableCell>
                        {/* Tot Ore */}
                        <TableCell sx={{ textAlign: 'center', backgroundColor: '#f5f5f5', fontWeight: 700, color: '#1976d2' }}>
                          {isRiposo ? '—' : calcOre(
                            isEditing ? editData.oraConsInizio : o.oraConsInizio,
                            isEditing ? editData.oraConsFine : o.oraConsFine
                          )}
                        </TableCell>
                        {/* Azioni */}
                        <TableCell sx={{ textAlign: 'center', padding: '2px 2px', whiteSpace: 'nowrap' }}>
                          {isEditing ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2 }} className="editing-buttons">
                              <Box sx={{ display: 'flex', gap: 0.3, justifyContent: 'center' }}>
                                <Tooltip title="Salva">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="success"
                                      disabled={isSaving}
                                      onClick={() => handleSaveEdit(o.id)}
                                      data-save-btn="true"
                                      sx={{ padding: '2px' }}
                                    >
                                      {isSaving ? <CircularProgress size={14} /> : <Save sx={{ fontSize: 16 }} />}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Annulla">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleCancelEdit(o.id)}
                                    sx={{ padding: '2px' }}
                                  >
                                    <Cancel sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              {timeErrors[o.id] && (
                                <Typography variant="caption" color="error"
                                  sx={{ display: 'block', fontSize: '0.58rem', lineHeight: 1.2, whiteSpace: 'normal', maxWidth: 80, textAlign: 'center' }}>
                                  {timeErrors[o.id]}
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', gap: 0.3, justifyContent: 'center' }}>
                              {!isFake && (
                                <Tooltip title="Duplica riga">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDuplicateRow(o)}
                                    sx={{ padding: '2px', color: '#388e3c' }}
                                  >
                                    <AddCircleOutline sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {isDuplicate && !isFake && (
                                <Tooltip title="Reimposta riga aggiunta a baseline RIPOSO">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleResetRowToBaseline(o)}
                                    sx={{ padding: '2px', color: '#c62828' }}
                                  >
                                    <DeleteOutline sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {/* Riga totale */}
                  <TableRow>
                    <TableCell
                      colSpan={11}
                      sx={{
                        textAlign: 'right',
                        fontWeight: 700,
                        backgroundColor: '#e3f2fd',
                        borderTop: '2px solid #1976d2',
                        fontSize: '0.8rem',
                      }}
                    >
                      TOTALE ORE:
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: 'center',
                        fontWeight: 700,
                        backgroundColor: '#e3f2fd',
                        borderTop: '2px solid #1976d2',
                        color: '#1976d2',
                        fontSize: '0.8rem',
                      }}
                    >
                      {totalOre}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* ── Statistiche + Controllo Orari (Admin) ── */}
          <Paper sx={{ p: 1.5, mt: 1.5, borderTop: '3px solid ' + PAGE_COLOR }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>

              {/* ── Colonna SINISTRA: Statistiche ── */}
              <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                  <AccessTime sx={{ color: PAGE_COLOR, fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Statistiche — {selectedMonth.format('MMMM YYYY')}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  {/* Statistiche ore */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {[
                      { label: 'Contratto', value: stats.oreContratto },
                      { label: 'Pianificate', value: stats.orePianificate },
                      { label: 'Consuntivate', value: stats.oreConsuntivate },
                      { label: 'Assenze Contr.', value: stats.assenzeContrattuali },
                      {
                        label: 'Attuale',
                        value: stats.attuale,
                        color: stats.attuale >= stats.oreContratto ? 'success.main' : 'error.main',
                      },
                      {
                        label: 'Differenza',
                        value: stats.differenzaAttuale,
                        color: stats.differenzaAttuale >= 0 ? 'success.main' : 'error.main',
                      },
                    ].map((it) => (
                      <Box
                        key={it.label}
                        sx={{
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          px: 1,
                          py: 0.5,
                          minWidth: 110,
                          backgroundColor: '#fafafa',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                          {it.label}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: it.color || 'inherit' }}>
                          {formatHoursNum(it.value)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Contratto ore giornaliere */}
                  {dipendenteInfo && (
                    <Box sx={{ borderLeft: '1px dashed #ccc', pl: 1.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                        Contratto giornaliero
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, auto)', gap: '2px 8px' }}>
                        {(['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'] as const).map((g, i) => {
                          const keys: (keyof Dipendente)[] = ['lunedi', 'martedi', 'mercoledi', 'giovedi', 'venerdi', 'sabato', 'domenica'];
                          const val = dipendenteInfo[keys[i]] as number;
                          return (
                            <Box key={g} sx={{ textAlign: 'center' }}>
                              <Typography variant="caption" sx={{ color: '#666', display: 'block', lineHeight: 1.1 }}>{g}</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.72rem' }}>{formatHoursNum(val)}</Typography>
                            </Box>
                          );
                        })}
                      </Box>
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Tipo contratto: </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          {(() => {
                            const df = dipendenteInfo.dataFineContratto?.slice(0, 10);
                            return !df || df === '2099-12-31' ? 'INDETERMINATO' : 'DETERMINATO';
                          })()}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* ── Colonna DESTRA: Controllo Orari (Admin/Coordinatore) ── */}
              {canManageDataControllo && selectedDipObj && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                  <Box sx={{
                    flex: '0 0 auto',
                    minWidth: 200,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1,
                    p: 1.5,
                  }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1976d2' }}>
                      Controllo Orari {controlloWarning && (
                        <Tooltip title="Ci sono turni lavorativi senza ore consolidate">
                          <span>⚠️</span>
                        </Tooltip>
                      )}
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                      <DatePicker
                        label="Data Controllo"
                        value={controlloDate}
                        onChange={(val) => setControlloDate(val)}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                      />
                    </LocalizationProvider>
                    <Button
                      variant="contained"
                      size="small"
                      fullWidth
                      disabled={!controlloDate || savingControllo}
                      onClick={handleSaveDataControllo}
                    >
                      {savingControllo ? <CircularProgress size={16} sx={{ color: 'white' }} /> : 'Imposta Data Controllo'}
                    </Button>
                    {currentUser?.ruolo === 'Admin' && (
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        onClick={() => { window.location.href = '/controllo-orari'; }}
                      >
                        Vai a Controllo Orari
                      </Button>
                    )}
                    <Dialog open={!!suggestionBanner} onClose={() => setSuggestionBanner(null)}>
                      <DialogTitle>Attenzione</DialogTitle>
                      <DialogContent>
                        <Typography variant="body2">
                          {suggestionBanner
                            ? `Ci sono dei turni non consolidati, l'ultimo turno è: ${dayjs(suggestionBanner.suggestedDate).format('DD/MM/YYYY')} (turno prima dell'ultimo non consolidato), vuoi impostare la data controllo a questo giorno?`
                            : ''}
                        </Typography>
                      </DialogContent>
                      <DialogActions>
                        <Button onClick={handleIgnoreSuggestionAndSaveControllo} color="warning" disabled={savingControllo}>No</Button>
                        <Button onClick={handleConfirmSuggestedDataControllo} variant="contained" color="warning" disabled={savingControllo}>Si</Button>
                      </DialogActions>
                    </Dialog>
                  </Box>
                </>
              )}
            </Box>
          </Paper>
        </>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
          <Typography color="text.secondary">
            Seleziona un dipendente per visualizzare l'orario del mese.
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default OrarioDipendenteCompact;
